import { EmptyState } from "../../app/Status";
import {
  TASK_STATUS_LABELS,
  TASK_STATUSES,
  type BuildingDto,
  type IssueSummary,
  type TaskDto,
  type TaskStatus,
  statusLabel,
} from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { useState } from "react";
import { IconPencil, IconPlus, IconTasks } from "../../app/icons";
import { Meta } from "../../app/Meta";
import { BookDialog, needsBooking, TaskMaterialsList } from "../materials/TaskMaterials";
import { useTaskRefresh } from "./useTasks";

export const fmtDue = (d: string | null) =>
  d
    ? new Date(`${d}T12:00:00`).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" })
    : null;

/** Takenoverzicht van een gebouw (prototype `renderTasks`). */
export function TaskBoard({
  building: b,
  tasks,
  issues,
  onOpenIssue,
  onEdit,
  onNew,
}: {
  building: BuildingDto;
  tasks: TaskDto[];
  issues: IssueSummary[];
  onOpenIssue: (id: string) => void;
  onEdit: (t: TaskDto) => void;
  onNew: () => void;
}) {
  const { data: me } = useMe();
  const toast = useToast();
  const refresh = useTaskRefresh(b.id);
  const canManage = can(me, "task.manage");
  const sorted = [...tasks].sort(
    (a, c) =>
      Number(a.status === "klaar") - Number(c.status === "klaar") || (a.dueDate ?? "9").localeCompare(c.dueDate ?? "9"),
  );

  const [book, setBook] = useState<{ task: TaskDto; finish: boolean } | null>(null);

  async function setStatus(t: TaskDto, status: TaskStatus) {
    // Naar Klaar met materialen die nog niet zijn afgeboekt: eerst afboeken (module Materialen).
    if (status === "klaar" && hasModule(me, "materials") && needsBooking(t)) return setBook({ task: t, finish: true });
    try {
      await api(`/tasks/${t.id}`, { method: "PATCH", body: { status } });
      await refresh();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Er ging iets mis");
    }
  }

  const open = tasks.filter((t) => t.status !== "klaar").length;
  return (
    <section className="tasks" aria-label="Taken">
      <div className="tablehead">
        <div className="grow">
          <p className="label">{b.name}</p>
          <h1>
            Taken <span className="muted num">{open}</span>
          </h1>
        </div>
        {canManage && (
          <span className="pageactions">
            <button type="button" className="btn primary" onClick={onNew}>
              <IconPlus size={18} aria-hidden="true" />
              Taak
            </button>
          </span>
        )}
      </div>
      <p className="muted small tintro">Een taak bundelt één of meer bevindingen voor één uitvoerder.</p>
      <div className="tgrid">
        {sorted.map((t) => {
          const its = t.issueIds
            .map((id) => issues.find((i) => i.id === id))
            .filter((i): i is IssueSummary => Boolean(i));
          const mayStatus = canManage || t.assigneeUserId === me?.user.id;
          return (
            <article className={`task ${t.status === "klaar" ? "done" : ""}`} key={t.id} aria-label={`Taak ${t.code}`}>
              <div className="h">
                <div className="grow">
                  <span className="code small">{t.code}</span>
                  <strong className="ttl">{t.title}</strong>
                </div>
                <label className="sr-only" htmlFor={`ts-${t.id}`}>
                  Status taak {t.code}
                </label>
                <select
                  id={`ts-${t.id}`}
                  className={`tstatus ts-${t.status}`}
                  value={t.status}
                  disabled={!mayStatus}
                  onChange={(e) => setStatus(t, e.target.value as TaskStatus)}
                >
                  {TASK_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {TASK_STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
                {canManage && (
                  <button
                    type="button"
                    className="btn small ghost ico"
                    aria-label="Bewerken"
                    title="Bewerken"
                    onClick={() => onEdit(t)}
                  >
                    <IconPencil size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
              <Meta
                className="small muted"
                items={[t.assigneeName ?? "Nog niet toegewezen", t.dueDate && `uiterlijk ${fmtDue(t.dueDate)}`]}
              />
              <ul className="tissues">
                {its.map((i) => (
                  <li key={i.id}>
                    <button type="button" onClick={() => onOpenIssue(i.id)}>
                      <span className={`chip prio prio-${i.priority}`}>{i.priority}</span>
                      <span className="code small">{i.code}</span>
                      <span className="grow">{i.title}</span>
                      <span className={`chip st-${i.status}`}>{statusLabel(i.status)}</span>
                    </button>
                  </li>
                ))}
                {its.length === 0 && <li className="small muted">Geen bevindingen gekoppeld.</li>}
              </ul>
              <TaskMaterialsList task={t} onBook={mayStatus ? () => setBook({ task: t, finish: false }) : undefined} />
            </article>
          );
        })}
        {sorted.length === 0 && (
          <EmptyState
            icon={IconTasks}
            title="Nog geen taken"
            text="Koppel bevindingen aan een taak vanuit de bevinding zelf, of maak hier een nieuwe."
          />
        )}
      </div>
      {book && (
        <BookDialog
          task={book.task}
          finish={
            book.finish
              ? async () => {
                  await api(`/tasks/${book.task.id}`, { method: "PATCH", body: { status: "klaar" } });
                }
              : undefined
          }
          onClose={() => setBook(null)}
          onDone={refresh}
        />
      )}
    </section>
  );
}
